import { applyOps, assetsMap, AssetSchema, type ImagePaint, type Paint } from '@flobi/artwork-core';
import type { OpenDocument } from '../useEditorDocument';
import { LOCAL_ORIGIN } from '../useEditorDocument';
import type { Binding } from './contracts';

/** The paint schema holds at most 32 results in one set; the oldest drop off. */
const MAX_RESULTS = 32;

export function eligible(node: ReturnType<OpenDocument['scene']['get']>): boolean {
  return !!node && !node.locked && ['rect', 'ellipse', 'polygon', 'star', 'path', 'boolean'].includes(node.type);
}

/** Attach to the captured node once. Assets stay saved when the page edit is undone. */
export function attachResults(open: OpenDocument, binding: Binding): 'applied' | 'already-applied' | 'unavailable' {
  if (binding.documentId !== open.documentId || binding.pageId !== open.pageId || !binding.runId || !binding.results.length) return 'unavailable';
  const node = open.scene.get(binding.targetId);
  if (!eligible(node) || open.scene.lockedHere(binding.targetId)) return 'unavailable';
  const fills = (node as { fills: Paint[] }).fills;
  const ids = binding.results.map(result => result.mediaAssetId);
  if (fills.some(fill => fill.type === 'image' && fill.resultSet && (fill.resultSet.generationId === binding.runId || ids.every(id => fill.resultSet!.assetIds.includes(id))))) return 'already-applied';
  // Missing storage identity never falls through to a provider URL or Drive-file ID.
  if (binding.results.some(result => !result.mediaAssetId || !result.width || !result.height || !result.url)) throw Error('The results are still being saved.');
  const assets = binding.results.map(result => [result.mediaAssetId, AssetSchema.parse({ kind: binding.request.kind, name: result.name,
    mediaAssetId: result.mediaAssetId, driveFileId: result.driveFileId, src: result.url, mime: result.mime,
    width: result.width, height: result.height, duration: result.duration })] as const);
  open.root.transact(() => assets.forEach(([id, asset]) => assetsMap(open.root).set(id, asset)), LOCAL_ORIGIN);
  const first = binding.results[0]!;
  // Another run into a shape that already shows results of this kind joins that set, as a
  // Flow node keeps its history: the new take is shown and ‹ › steps back to the earlier ones.
  const current = fills.findLast(fill => fill.type === 'image' && fill.visible && fill.resultSet?.kind === binding.request.kind) as ImagePaint | undefined;
  const kept = current?.resultSet ? current.resultSet.assetIds.filter(id => !ids.includes(id)) : [];
  const assetIds = [...kept, ...ids].slice(-MAX_RESULTS);
  const active = assetIds.length - ids.length;
  const resultSet = { generationId: binding.runId, kind: binding.request.kind, assetIds, active, ...(binding.request.kind === 'video' ? { duration: first.duration } : {}) };
  const next = current
    ? fills.map(fill => fill === current ? { ...current, assetId: first.mediaAssetId, resultSet } : fill)
    : [...fills, { type: 'image', assetId: first.mediaAssetId, fit: 'crop', scale: 1, visible: true, opacity: 1, blendMode: 'normal', resultSet } satisfies ImagePaint];
  open.undo.stopCapturing();
  const outcome = applyOps(open.page, [{ op: 'set', id: binding.targetId, props: { fills: next } }], { scene: open.scene, origin: LOCAL_ORIGIN });
  open.undo.stopCapturing();
  if (!outcome.ok) throw Error('The generated fill could not be added. Your results are saved.');
  return 'applied';
}

export function chooseResult(paint: ImagePaint, active: number, duration?: number): ImagePaint {
  const set = paint.resultSet;
  if (!set || !set.assetIds[active]) return paint;
  return { ...paint, assetId: set.assetIds[active]!, resultSet: { ...set, active, ...(duration ? { duration } : {}) } };
}

/**
 * Keep This: the shape keeps one take and its history forgets the rest (they stay saved in My
 * Stuff). The set stays, one long, so the next run into the shape joins it again.
 */
export function keepResult(paint: ImagePaint, index: number, duration?: number): ImagePaint {
  const set = paint.resultSet, id = set?.assetIds[index];
  if (!set || !id) return paint;
  return { ...paint, assetId: id, resultSet: { ...set, assetIds: [id], active: 0, ...(duration ? { duration } : {}) } };
}

/** One take as a plain picture fill, for a copy of the shape that holds it alone. */
export function takeAlone(paint: ImagePaint, index: number): ImagePaint {
  const chosen = chooseResult(paint, index);
  const { resultSet: _set, ...plain } = chosen;
  return plain;
}
